/* =========================================================================
   LEVORA — levora-interactive.js
   Vanilla puro. Sin dependencias, sin polyfills, sin build.

   Carga con `defer`: se ejecuta despues de parsear el HTML y antes de
   DOMContentLoaded, asi que nunca bloquea el primer pintado.

   Regla que se sigue en todo el archivo: el HTML que sale del servidor ya
   esta completo y es usable. El JS solo mejora. Si algo falla aqui, la
   pagina sigue vendiendo: el formulario del pedido lo pinta EasySell y no
   depende de este archivo, el carrusel se desliza con el scroll nativo del
   navegador y el acordeon deja las respuestas a la vista.
   ========================================================================= */
(function () {
  'use strict';

  /* -----------------------------------------------------------------------
     EL PURCHASE DE EASYSELL, Y POR QUE SE PERDIA

     EasySell crea el pedido, dispara el evento de compra y acto seguido
     manda el navegador a `order_status_url`, que esta fuera de este tema.

     La baliza de un pixel es una peticion en vuelo. Si la pagina cambia
     antes de que salga, el navegador la cancela: la venta ocurre, el pedido
     entra en Shopify, y Meta no se entera de nada. Con contrareembolso eso
     es lo peor que puede pasar, porque el algoritmo optimiza justo por ese
     evento.

     La propia aplicacion hace `await` sobre esta funcion antes de redirigir
     -es un gancho que deja a proposito-, asi que basta con devolver una
     promesa que tarde un poco. Mientras, EasySell ya esta enseñando su
     estado de carga, de modo que el comprador no ve la pagina congelada.

     Aqui NO se dispara ningun evento: solo se le da tiempo al que EasySell
     ya ha mandado. Dispararlo otra vez contaria dos veces la misma venta.
     ----------------------------------------------------------------------- */
  var MARGEN_PIXEL = 1500;

  if (typeof window.Promise === 'function' &&
      typeof window.es_hook_before_redirect !== 'function') {
    window.es_hook_before_redirect = function () {
      return new window.Promise(function (listo) {
        window.setTimeout(listo, MARGEN_PIXEL);
      });
    };
  }

  var REDUCED = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false };

  /* ======================================================================
     0. Cuenta atras de la barra superior

     El plazo se guarda por visitante en `localStorage`. Si se recalculara en
     cada carga, bastaria con recargar para ver que el contador vuelve a
     empezar: se pierde la poca credibilidad que tenia y ademas es una
     practica comercial desleal tipificada.

     El reloj ya viene pintado desde el servidor, asi que si esto no llega a
     ejecutarse se ve una hora fija en vez de un hueco. Feo, pero sin salto.
     ====================================================================== */
  function initCountdown() {
    var cajas = Array.prototype.slice.call(document.querySelectorAll('[data-countdown]'));
    if (!cajas.length) return;

    function dosDigitos(n) { return (n < 10 ? '0' : '') + n; }

    cajas.forEach(function (caja) {
      var segundos = parseInt(caja.getAttribute('data-seconds'), 10) || 0;
      var clave = caja.getAttribute('data-key') || 'levora-cd';
      var reloj = caja.querySelector('[data-cd-clock]');
      if (!segundos || !reloj) return;

      /* El almacenamiento puede fallar (modo privado, cookies bloqueadas).
         Si falla, el contador funciona igual: solo pierde la memoria entre
         paginas. Nunca debe tumbar el resto del JS de la ficha. */
      var fin = 0;
      try {
        var guardado = parseInt(window.localStorage.getItem(clave), 10);
        if (guardado && guardado > Date.now()) fin = guardado;
      } catch (e) { /* sin almacenamiento */ }

      if (!fin) {
        fin = Date.now() + segundos * 1000;
        try { window.localStorage.setItem(clave, String(fin)); } catch (e) { /* idem */ }
      }

      var segs = reloj.querySelectorAll('.announce__seg');
      if (segs.length < 3) return;

      var timer = null;

      function pintar() {
        var resto = Math.max(0, Math.floor((fin - Date.now()) / 1000));
        var h = Math.floor(resto / 3600);
        var m = Math.floor((resto % 3600) / 60);
        var sg = resto % 60;

        segs[0].textContent = dosDigitos(h);
        segs[1].textContent = dosDigitos(m);
        segs[2].textContent = dosDigitos(sg);

        if (resto <= 0 && timer) {
          clearInterval(timer);
          caja.classList.add('is-done');
        }
      }

      pintar();
      timer = setInterval(pintar, 1000);

      /* Al volver de otra pestana el intervalo puede haberse retrasado: se
         repinta al recuperar el foco para que no se vea desfasado. */
      document.addEventListener('visibilitychange', function () {
        if (!document.hidden) pintar();
      });
    });
  }

  /* ======================================================================
     2. Barra fija de movil

     Aparece cuando el formulario del pedido sale de la pantalla. Se observa
     el FORMULARIO, no un centinela suelto: si manana cambia el maquetado, no
     hay forma de que los dos se desincronicen.

     El boton es un enlace a #pedido, asi que devuelve al formulario aunque
     este archivo no llegue a ejecutarse. Lo unico que aporta el JS es que la
     barra no este siempre a la vista comiendo pantalla.
     ====================================================================== */
  function initSticky() {
    var barra = document.querySelector('[data-sticky-cta]');
    var ancla = document.querySelector('[data-form-cod]');
    if (!barra || !ancla) return;

    if (!('IntersectionObserver' in window)) {
      /* Sin soporte, mejor no mostrarla nunca que mostrarla siempre. */
      return;
    }

    var obs = new IntersectionObserver(function (entradas) {
      entradas.forEach(function (e) {
        /* Solo cuando el formulario ha quedado POR ENCIMA de la pantalla. Si
           se mirara unicamente `isIntersecting`, la barra saltaria tambien al
           cargar una pagina que arranca por debajo del formulario. */
        var fuera = !e.isIntersecting && e.boundingClientRect.top < 0;
        barra.classList.toggle('is-on', fuera);
      });
    }, { threshold: 0, rootMargin: '0px' });

    obs.observe(ancla);
  }

  /* ======================================================================
     3. Galeria: carrusel de arrastre

     El deslizamiento lo hace el navegador. Aqui solo se sincroniza que punto
     y que miniatura estan marcados, y se salta a una foto al pulsarlos.

     No se mide nada al arrancar: el estado inicial ya viene marcado desde el
     servidor. Leer geometria durante la carga fuerza un recalculo de
     maquetacion justo cuando se esta pintando la primera pantalla.
     ====================================================================== */
  function initGallery(root) {
    var rail = root.querySelector('[data-gal-rail]');
    if (!rail) return;

    var slides = Array.prototype.slice.call(rail.querySelectorAll('[data-gal-slide]'));
    if (slides.length < 2) return;

    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-gal-dot]'));
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-thumb]'));

    /* Cual queda mas centrada en el carril. Se compara por centros y no por
       el borde izquierdo para que valga igual con `scroll-snap-align: center`
       y con margenes distintos a los lados. */
    function activa() {
      var caja = rail.getBoundingClientRect();
      var centro = caja.left + caja.width / 2;
      var mejor = 0;
      var min = Infinity;
      for (var i = 0; i < slides.length; i++) {
        var c = slides[i].getBoundingClientRect();
        var d = Math.abs(c.left + c.width / 2 - centro);
        if (d < min) { min = d; mejor = i; }
      }
      return mejor;
    }

    function pintar(i) {
      for (var d = 0; d < dots.length; d++) {
        dots[d].setAttribute('aria-selected', d === i ? 'true' : 'false');
      }
      for (var t = 0; t < thumbs.length; t++) {
        thumbs[t].setAttribute('aria-selected', t === i ? 'true' : 'false');
      }
    }

    var pendiente = false;
    function marcar() {
      pendiente = false;
      pintar(activa());
    }

    /* El evento de scroll se dispara decenas de veces por gesto. Sin este
       freno se recalcularia la posicion en cada uno y el arrastre se
       engancharia en moviles modestos. */
    rail.addEventListener('scroll', function () {
      if (pendiente) return;
      pendiente = true;
      requestAnimationFrame(marcar);
    }, { passive: true });

    /* Red de seguridad al terminar el gesto. Si por lo que sea no se llega a
       ejecutar el fotograma de arriba, el punto acaba marcado igual: lo que
       no puede pasar es que dejes de deslizar y el indicador senale otra
       foto. Donde `scrollend` no existe, este oyente simplemente no salta. */
    rail.addEventListener('scrollend', marcar);

    function ir(i) {
      var s = slides[i];
      if (!s) return;

      var destino = s.offsetLeft - (rail.clientWidth - s.offsetWidth) / 2;
      var antes = rail.scrollLeft;

      /* El indicador salta al destino ANTES de moverse. Si se midiera despues
         de lanzar el desplazamiento suave, se leeria la posicion de partida y
         el punto marcaria la foto de la que vienes durante toda la
         animacion. */
      pintar(i);

      /* `scrollTo` con opciones no existe en navegadores antiguos: alli lanza
         o se ignora sin avisar. */
      try {
        rail.scrollTo({ left: destino, behavior: REDUCED.matches ? 'auto' : 'smooth' });
      } catch (e) {
        rail.scrollLeft = destino;
      }

      /* Y si el desplazamiento suave no llega a ejecutarse —una pestana en
         segundo plano, el sistema con las animaciones desactivadas—, se hace
         el salto seco. Un salto brusco es peor que una transicion; no llegar
         a la foto que has pulsado es mucho peor que las dos cosas. */
      setTimeout(function () {
        if (Math.abs(rail.scrollLeft - antes) < 2 && Math.abs(destino - antes) > 2) {
          rail.scrollLeft = destino;
        }
      }, 350);
    }

    function conectar(botones) {
      botones.forEach(function (b, i) {
        b.addEventListener('click', function () { ir(i); });
        b.addEventListener('keydown', function (e) {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          e.preventDefault();
          var n = (i + (e.key === 'ArrowRight' ? 1 : -1) + botones.length) % botones.length;
          botones[n].focus();
          ir(n);
        });
      });
    }

    conectar(dots);
    conectar(thumbs);

    /* Con el carril enfocado, las flechas tambien mueven las fotos. */
    rail.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      e.preventDefault();
      ir((activa() + (e.key === 'ArrowRight' ? 1 : -1) + slides.length) % slides.length);
    });
  }

  /* ======================================================================
     4. Acordeon FAQ

     `max-height` se fija al alto REAL medido del contenido, no a un numero
     grande a ojo: con un valor inventado la transicion tarda lo mismo para
     una respuesta de dos lineas que para una de diez, y se nota.

     Se recalcula al redimensionar porque el texto reflowa y el alto cambia.
     ====================================================================== */
  function initFaq(root) {
    var items = Array.prototype.slice.call(root.querySelectorAll('[data-faq-item]'));
    if (!items.length) return;

    /* Medir el alto es una LECTURA de geometria; poner `max-height` es una
       escritura. Si se intercalan —cerrar las otras y despues medir esta—,
       el navegador tiene que recalcular la maquetacion en mitad del clic
       para poder responder a la medida. Es el reflujo forzado que sale en
       PageSpeed. Aqui se lee primero y se escribe despues, siempre. */
    function alto(item) {
      var panel = item.querySelector('[data-faq-a]');
      var inner = panel && panel.firstElementChild;
      return inner ? inner.offsetHeight : 0;
    }

    function pintar(item, si, px) {
      var boton = item.querySelector('[data-faq-q]');
      var panel = item.querySelector('[data-faq-a]');
      if (!boton || !panel) return;
      boton.setAttribute('aria-expanded', si ? 'true' : 'false');
      panel.style.maxHeight = si ? px + 'px' : '0px';
    }

    items.forEach(function (item) {
      var boton = item.querySelector('[data-faq-q]');
      if (!boton) return;

      boton.addEventListener('click', function () {
        var abierto = boton.getAttribute('aria-expanded') === 'true';

        /* Lectura primero. */
        var px = abierto ? 0 : alto(item);

        /* Y ahora todas las escrituras seguidas. Acordeon de uno en uno: dos
           respuestas largas abiertas a la vez empujan la pagina y obligan a
           buscar donde estabas. */
        items.forEach(function (otro) { if (otro !== item) pintar(otro, false, 0); });
        pintar(item, !abierto, px);
      });

      /* Al arrancar solo se cierra: escribir '0px' no obliga a medir nada. */
      pintar(item, false, 0);
    });

    var t;
    window.addEventListener('resize', function () {
      clearTimeout(t);
      t = setTimeout(function () {
        var abiertos = items.filter(function (item) {
          var boton = item.querySelector('[data-faq-q]');
          return boton && boton.getAttribute('aria-expanded') === 'true';
        });
        var medidas = abiertos.map(alto);
        abiertos.forEach(function (item, i) { pintar(item, true, medidas[i]); });
      }, 150);
    }, { passive: true });
  }

  /* ======================================================================
     5. Muro de resenas 9:16

     La tarjeta mas cercana al centro del carril se agranda. Se calcula en el
     evento de scroll con throttle por rAF: es una lectura de geometria por
     fotograma como mucho, que es lo que el navegador va a hacer de todos
     modos al pintar.
     ====================================================================== */
  function initReviews(root) {
    var rail = root.querySelector('[data-rev-rail]');
    if (!rail) return;

    var cards = Array.prototype.slice.call(rail.querySelectorAll('[data-rev-card]'));
    if (cards.length < 2) return;

    var pendiente = false;

    function foco() {
      pendiente = false;
      var caja = rail.getBoundingClientRect();
      var centro = caja.left + caja.width / 2;
      var mejor = null;
      var min = Infinity;

      for (var i = 0; i < cards.length; i++) {
        var c = cards[i].getBoundingClientRect();
        var d = Math.abs(c.left + c.width / 2 - centro);
        if (d < min) { min = d; mejor = cards[i]; }
      }

      for (var j = 0; j < cards.length; j++) {
        cards[j].classList.toggle('is-focus', cards[j] === mejor);
      }
    }

    rail.addEventListener('scroll', function () {
      if (pendiente) return;
      pendiente = true;
      requestAnimationFrame(foco);
    }, { passive: true });

    window.addEventListener('resize', foco, { passive: true });

    /* Arranque: centrar la segunda tarjeta deja ver que hay mas a los lados,
       que es lo que invita a arrastrar. Sin animacion si se pide menos
       movimiento. */
    var inicio = cards[1] || cards[0];
    rail.scrollTo({
      left: inicio.offsetLeft - (rail.clientWidth - inicio.offsetWidth) / 2,
      behavior: REDUCED.matches ? 'auto' : 'smooth'
    });

    foco();
  }

  /* ====================================================================== */
  function arrancar() {
    initFormCheck();
    initCountdown();
    initSticky();

    var pdp = document.querySelector('[data-pdp]');
    if (pdp) initGallery(pdp);

    var faq = document.querySelector('[data-faq]');
    if (faq) initFaq(faq);

    /* El muro de resenas esta muy por debajo del pliegue y su arranque lee
       geometria, lo que forzaria un recalculo de maquetacion en plena carga.
       Se espera a que el carril se acerque a la pantalla, con una red de
       seguridad por si el observador no entrega avisos en algun contexto. */
    var rev = document.querySelector('[data-reviews]');
    if (rev) {
      var muroListo = false;
      var arrancaMuro = function () {
        if (muroListo) return;
        muroListo = true;
        initReviews(rev);
      };

      if ('IntersectionObserver' in window) {
        var espera = new IntersectionObserver(function (entradas, obs) {
          if (!entradas[0].isIntersecting) return;
          obs.disconnect();
          arrancaMuro();
        }, { rootMargin: '400px 0px' });
        espera.observe(rev);
      }

      setTimeout(arrancaMuro, 3000);
    }
  }

  /* ======================================================================
     EL HUECO DEL FORMULARIO SE HA QUEDADO VACIO

     El bloque de EasySell no pinta nada por si solo: solo deja una marca
     <easysell-form> vacia. Quien la rellena es el JavaScript del app embed.

     Subir un tema en ZIP crea un tema NUEVO, y los app embeds viven en el
     settings_data.json de cada tema: el embed se queda apagado en cada
     subida. El bloque sigue puesto, la pagina no da ningun error, y la
     ficha se publica sin caja.

     Esto solo corre en el personalizador. El cliente nunca lo ve.
     ====================================================================== */
  function initFormCheck() {
    var hueco = document.querySelector('[data-form-check]');
    if (!hueco) return;

    setTimeout(function () {
      if (hueco.offsetHeight > 40) return;

      var aviso = document.createElement('p');
      aviso.className = 'form__falta';
      aviso.innerHTML =
        '<strong>El bloque está puesto, pero el formulario no se ha dibujado.</strong> ' +
        'Casi siempre es el <em>app embed</em> apagado: abre ' +
        '<em>Configuración de la aplicación</em> y activa <strong>EasySell COD Form</strong>. ' +
        'Al subir un tema nuevo se apaga solo.';
      hueco.parentNode.insertBefore(aviso, hueco);
    }, 4000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', arrancar);
  } else {
    arrancar();
  }
})();
